/* ============================================================
   science-page.js — BioActive+ Science page (Shopify Dawn handoff)
   Vanilla JS, no dependencies. Powers every interaction that the
   React prototype did: scroll reveal, count-up stats, the generated
   infographic diagrams, the ingredient ⇄ sourcing-map explorer,
   study / press accordions, the "plug in your biology" selector,
   the code typer, mobile read-more, nav + sticky bar.
   Dynamic collections (ingredients, sourcing map, future goals) are
   rendered from the JSON island #sci-data inside the section.
   ============================================================ */
(function () {
  "use strict";

  function init() {
    var root = document.querySelector(".sci2");
    if (!root) return;
    var DATA = readData();
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var anim = root.classList.contains("sci2--anim") && !reduce;

    setupReveal(root, anim);
    setupCountUp(anim);
    buildDiagrams(root);
    setupBars(anim);
    setupAnnounce(root);
    setupNav(root);
    setupAccordions(root);
    if (DATA) {
      buildIngredients(root, DATA.ingredients || []);
      buildFuture(root, DATA.goals || []);
    }
    setupTyper(root);
    setupClamp(root);
    setupShopBar(root);
  }

  function readData() {
    var el = document.getElementById("sci-data");
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ---------- scroll reveal (scroll + timer checks: robust everywhere) ---------- */
  function setupReveal(root, anim) {
    var items = [].slice.call(root.querySelectorAll(".rv"));
    if (!anim) { items.forEach(function (el) { el.classList.add("in"); }); return; }
    function check() {
      var vh = window.innerHeight || 0, trig = vh - vh * 0.08;
      for (var i = items.length - 1; i >= 0; i--) {
        var r = items[i].getBoundingClientRect();
        if (r.top < trig && r.bottom > -40) { items[i].classList.add("in"); items.splice(i, 1); }
      }
    }
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    [0, 60, 180, 400, 800, 1400].forEach(function (ms) { setTimeout(check, ms); });
    requestAnimationFrame(function () { requestAnimationFrame(check); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
  }

  /* fire cb once when el is in view — IO if available, always a timeout fallback */
  function onView(el, cb) {
    var done = false; function run() { if (done) return; done = true; cb(); }
    if (typeof IntersectionObserver !== "undefined") {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { io.disconnect(); run(); } });
      }, { threshold: 0.25 });
      io.observe(el);
    }
    setTimeout(run, 1500);
  }

  /* ---------- count-up numbers ---------- */
  function setupCountUp(anim) {
    var els = [].slice.call(document.querySelectorAll("[data-countup]"));
    els.forEach(function (el) {
      var raw = el.textContent.trim();
      var m = raw.match(/^([^0-9]*)([0-9][0-9,\.]*)(.*)$/);
      if (!m) return;
      var pre = m[1], num = parseFloat(m[2].replace(/,/g, "")), post = m[3];
      var isInt = m[2].indexOf(".") === -1;
      var hasComma = m[2].indexOf(",") !== -1;
      var fmt = function (v) {
        var s = isInt ? Math.round(v) : v.toFixed(1);
        if (hasComma) s = Number(s).toLocaleString("en-US");
        return pre + s + post;
      };
      if (!anim) { el.textContent = fmt(num); return; }
      el.textContent = fmt(0);
      onView(el, function () {
        var t0 = performance.now(), dur = 1100;
        (function tick(t) {
          var p = Math.min(1, (t - t0) / dur), k = 1 - Math.pow(1 - p, 3);
          el.textContent = fmt(num * k);
          if (p < 1) requestAnimationFrame(tick); else el.textContent = fmt(num);
        })(t0);
      });
    });
  }

  /* ---------- infographic diagrams (built as SVG) ---------- */
  function buildDiagrams(root) {
    var map = { peptide: peptideSVG, collagen: collagenSVG, antioxidant: antioxidantSVG };
    [].slice.call(root.querySelectorAll(".dgm[data-diagram]")).forEach(function (host) {
      var fn = map[host.getAttribute("data-diagram")];
      if (!fn) return;
      host.innerHTML = fn();
      var svg = host.querySelector("svg");
      playOnView(svg);
    });
  }
  function playOnView(el) { onView(el, function () { el.classList.add("play"); }); }
  function peptideSVG() {
    var n = 7, x0 = 30, x1 = 290, y = 58, xs = [], ys = [], i;
    for (i = 0; i < n; i++) { xs.push(x0 + i * (x1 - x0) / (n - 1)); ys.push(y + (i % 2 ? -10 : 10)); }
    var mid = [2, 3, 4], s = '<svg class="dgm-svg" viewBox="0 0 320 120" role="img" aria-label="Amino acids linked into a peptide chain">';
    for (i = 0; i < n - 1; i++) s += '<line class="d-bond" x1="' + xs[i] + '" y1="' + ys[i] + '" x2="' + xs[i + 1] + '" y2="' + ys[i + 1] + '" style="transition-delay:' + (i * 70) + 'ms"/>';
    s += '<path class="d-bracket" d="M ' + (xs[2] - 5) + ' 94 q 0 9 9 9 H ' + (xs[4] - 4) + ' q 9 0 9 -9" fill="none"/>';
    s += '<text class="d-lbl d-lbl--accent" x="' + ((xs[2] + xs[4]) / 2) + '" y="117" text-anchor="middle">peptide</text>';
    for (i = 0; i < n; i++) s += '<circle class="d-node' + (mid.indexOf(i) >= 0 ? " hot" : "") + '" cx="' + xs[i] + '" cy="' + ys[i] + '" r="9.5" style="transition-delay:' + (120 + i * 70) + 'ms"/>';
    s += '<text class="d-lbl" x="' + xs[0] + '" y="' + (ys[0] - 17) + '" text-anchor="middle">amino acid</text></svg>';
    return s;
  }
  function collagenSVG() {
    var W = 320, midY = 52, amp = 19, steps = 64, period = 70, phases = [0, 2.0944, 4.1888], s = '<svg class="dgm-svg" viewBox="0 0 320 120" role="img" aria-label="Collagen triple-helix structure">';
    phases.forEach(function (ph, i) {
      var dd = "", j;
      for (j = 0; j <= steps; j++) { var x = j / steps * (W - 30) + 15, yv = midY + amp * Math.sin(x / period * Math.PI * 2 + ph); dd += (j ? " L " : "M ") + x.toFixed(1) + " " + yv.toFixed(1); }
      s += '<path class="d-helix' + (i === 1 ? " hot" : "") + '" d="' + dd + '" fill="none" style="transition-delay:' + (i * 220) + 'ms"/>';
    });
    s += '<line class="d-bond soft" x1="15" y1="96" x2="305" y2="96"/>';
    s += '<text class="d-lbl" x="15" y="113" text-anchor="start">fibre rebuilt →</text>';
    s += '<text class="d-lbl d-lbl--accent" x="305" y="113" text-anchor="end">triple helix</text></svg>';
    return s;
  }
  function antioxidantSVG() {
    return '<svg class="dgm-svg" viewBox="0 0 320 120" role="img" aria-label="An antioxidant donating an electron to neutralise a free radical">'
      + '<circle class="d-ring alert" cx="58" cy="52" r="22"/><circle class="d-node" cx="58" cy="52" r="7"/><circle class="d-spark" cx="78" cy="36" r="3.4"/><text class="d-lbl" x="58" y="96" text-anchor="middle">free radical</text>'
      + '<circle class="d-node hot" cx="160" cy="52" r="12"/><text class="d-mini" x="160" y="56" text-anchor="middle">e⁻</text><text class="d-lbl d-lbl--accent" x="160" y="96" text-anchor="middle">antioxidant</text>'
      + '<line class="d-bond flow" x1="138" y1="52" x2="98" y2="52"/><path class="d-bond" d="M 102 47 L 96 52 L 102 57" fill="none"/>'
      + '<line class="d-bond soft" x1="182" y1="52" x2="234" y2="52"/><path class="d-bond" d="M 230 47 L 236 52 L 230 57" fill="none"/>'
      + '<circle class="d-ring" cx="278" cy="52" r="20"/><circle class="d-node" cx="278" cy="52" r="7"/><path class="d-check" d="M 270 52 l 5 6 l 10 -13" fill="none"/><text class="d-lbl" x="278" y="96" text-anchor="middle">stabilised</text></svg>';
  }

  /* ---------- animated bars (fill on view) ---------- */
  function setupBars(anim) {
    var bars = [].slice.call(document.querySelectorAll(".barh__fill[data-w]"));
    bars.forEach(function (fill) {
      var target = fill.getAttribute("data-w");
      if (!anim) { fill.style.width = target; return; }
      fill.style.width = "0%";
      onView(fill.closest(".barh") || fill, function () { fill.style.width = target; });
    });
  }

  /* ---------- announcement rotator ---------- */
  function setupAnnounce(root) {
    var bar = root.querySelector(".annbar");
    if (!bar) return;
    var msgEl = bar.querySelector(".annbar__msg");
    var items;
    try { items = JSON.parse(bar.getAttribute("data-items")); } catch (e) { items = null; }
    if (!items || items.length < 2) return;
    var i = 0;
    function show(n) { i = (n + items.length) % items.length; msgEl.textContent = items[i]; msgEl.style.animation = "none"; void msgEl.offsetWidth; msgEl.style.animation = ""; }
    var prev = bar.querySelector('[data-ann="prev"]'), next = bar.querySelector('[data-ann="next"]');
    if (prev) prev.addEventListener("click", function () { show(i - 1); restart(); });
    if (next) next.addEventListener("click", function () { show(i + 1); restart(); });
    var timer = setInterval(function () { show(i + 1); }, 3600);
    function restart() { clearInterval(timer); timer = setInterval(function () { show(i + 1); }, 3600); }
  }

  /* ---------- mobile nav ---------- */
  function setupNav(root) {
    var nav = root.querySelector(".nav");
    if (!nav) return;
    var btn = nav.querySelector(".nav__hamb");
    if (btn) btn.addEventListener("click", function () { nav.classList.toggle("open"); });
    [].slice.call(nav.querySelectorAll(".nav__link")).forEach(function (a) {
      a.addEventListener("click", function () { nav.classList.remove("open"); });
    });
  }

  /* ---------- generic accordions (studies, press) ---------- */
  function setupAccordions(root) {
    [].slice.call(root.querySelectorAll("[data-accordion]")).forEach(function (card) {
      card.addEventListener("click", function (e) {
        if (e.target.closest("a")) return;
        card.classList.toggle("open");
        var t = card.querySelector(".scard__toggle span"), chev = card.querySelector(".scard__chev");
        if (t) t.textContent = card.classList.contains("open") ? "Hide methodology" : "View methodology";
        if (chev) chev.textContent = card.classList.contains("open") ? "–" : "+";
        var cue = card.querySelector(".pcard__cue");
        if (cue) cue.textContent = card.classList.contains("open") ? "Close" : "Read";
      });
    });
  }

  /* ---------- ingredients ⇄ sourcing map ---------- */
  function hueVar(h) { return "var(--c-" + h + ")"; }
  function buildIngredients(root, items) {
    if (!items.length) return;
    var rail = root.querySelector("#ingr-rail");
    var detail = root.querySelector("#ingr-detail");
    var mapStage = root.querySelector("#map-stage");
    var traceList = root.querySelector("#trace-list");
    var active = null;
    var hub = { x: 0.82, y: 0.86 };

    /* chips */
    if (rail) { rail.innerHTML = ""; }
    if (detail) { detail.innerHTML = ""; }
    if (rail) items.forEach(function (it, i) {
      var b = document.createElement("button");
      b.className = "ichip"; b.style.setProperty("--sa", hueVar(it.hue)); b.setAttribute("data-id", it.id);
      var phHtml = it.img
        ? '<span class="slot ichip__ph"><img src="' + esc(it.img) + '" alt="' + esc(it.name) + '" loading="lazy" style="width:100%;height:100%;object-fit:cover;display:block"></span>'
        : '<span class="slot ichip__ph"><span class="slot__tag">Ingredient — ' + esc(it.name) + '</span></span>';
      b.innerHTML = '<span class="ichip__num">' + pad(i + 1) + '</span>'
        + phHtml
        + '<div class="ichip__name">' + esc(it.name) + '</div>'
        + '<div class="ichip__meta"><span class="ichip__dot"></span>' + esc(it.origin) + ' · ' + esc(it.tag) + '</div>';
      b.addEventListener("click", function () { setActive(active === it.id ? null : it.id); });
      rail.appendChild(b);
    });

    /* map constellation */
    if (mapStage) {
      var arc = function (n) {
        var x1 = n.coord[0] * 100, y1 = n.coord[1] * 100, x2 = hub.x * 100, y2 = hub.y * 100;
        var mx = (x1 + x2) / 2, my = (y1 + y2) / 2 - 16;
        return "M " + x1 + " " + y1 + " Q " + mx + " " + my + " " + x2 + " " + y2;
      };
      var svg = '<div class="mapgrid"></div><svg class="mapsvg" viewBox="0 0 100 100" preserveAspectRatio="none">';
      items.forEach(function (n) { svg += '<path class="maparc" data-id="' + n.id + '" d="' + arc(n) + '"/>'; });
      svg += '</svg>';
      svg += '<div class="mnode mnode--hub" style="left:' + (hub.x * 100) + '%;top:' + (hub.y * 100) + '%"><span class="mnode__dot"></span><span class="mnode__label flip">NZ Lab</span></div>';
      items.forEach(function (n, i) {
        var flip = n.coord[0] > 0.6;
        svg += '<div class="mnode" data-id="' + n.id + '" style="left:' + (n.coord[0] * 100) + '%;top:' + (n.coord[1] * 100) + '%;--sa:' + hueVar(n.hue) + '"><span class="mnode__dot"></span><span class="mnode__label' + (flip ? " flip" : "") + '">' + pad(i + 1) + '</span></div>';
      });
      mapStage.innerHTML = svg;
      [].slice.call(mapStage.querySelectorAll(".mnode[data-id]")).forEach(function (node) {
        var id = node.getAttribute("data-id");
        node.addEventListener("mouseenter", function () { setActive(id); });
        node.addEventListener("click", function () { setActive(active === id ? null : id); });
      });
    }

    /* origins list (links to #ingredients) */
    if (traceList) {
      items.forEach(function (n, i) {
        var a = document.createElement("a");
        a.className = "torigin"; a.href = "#ingredients"; a.style.setProperty("--sa", hueVar(n.hue)); a.setAttribute("data-id", n.id);
        a.innerHTML = '<span class="torigin__n">' + pad(i + 1) + '</span><span class="torigin__dot"></span>'
          + '<span class="torigin__txt"><span class="torigin__city">' + esc(n.city) + ', ' + esc(n.origin) + '</span><span class="torigin__ing">' + esc(n.name) + ' · ' + esc(n.tag) + '</span></span>'
          + '<span class="torigin__cue">Trace</span>'
          + '<svg class="torigin__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        a.addEventListener("mouseenter", function () { setActive(n.id); });
        a.addEventListener("click", function () { setActive(n.id); });
        traceList.appendChild(a);
      });
      var dest = document.createElement("div");
      dest.className = "trace__dest";
      dest.innerHTML = '<span class="trace__destdot"></span><span>Blended &amp; formulated in <b>New Zealand</b></span>';
      traceList.appendChild(dest);
    }

    function setActive(id) {
      active = id;
      var cur = null; items.forEach(function (x) { if (x.id === id) cur = x; });
      if (rail) [].slice.call(rail.children).forEach(function (c) { c.classList.toggle("active", c.getAttribute("data-id") === id); });
      if (traceList) [].slice.call(traceList.querySelectorAll(".torigin")).forEach(function (c) { c.classList.toggle("on", c.getAttribute("data-id") === id); });
      if (mapStage) {
        [].slice.call(mapStage.querySelectorAll(".mnode[data-id]")).forEach(function (nd) { nd.classList.toggle("active", nd.getAttribute("data-id") === id); });
        [].slice.call(mapStage.querySelectorAll(".maparc")).forEach(function (p) {
          var on = p.getAttribute("data-id") === id;
          p.style.opacity = id ? (on ? 0.9 : 0.07) : 0.32;
          if (on && cur) { p.style.stroke = "var(--c-" + cur.hue + ")"; p.style.strokeDasharray = "none"; } else { p.style.stroke = ""; p.style.strokeDasharray = ""; }
        });
      }
      if (detail) renderDetail(detail, cur);
    }
    function renderDetail(host, cur) {
      host.classList.toggle("open", !!cur);
      if (cur) host.style.setProperty("--sa", hueVar(cur.hue));
      host.innerHTML = !cur ? "" : '<div class="idetail__inner"><div class="idetail__pad">'
        + '<div class="idetail__top"><span class="idetail__origin">' + esc(cur.city) + ', ' + esc(cur.origin) + '</span><span class="idetail__tag">' + esc(cur.tag) + '</span></div>'
        + '<div class="idetail__name">' + esc(cur.name) + '</div>'
        + '<div class="idetail__benefit">' + esc(cur.benefit) + '</div>'
        + '<div class="idetail__body">' + esc(cur.detail) + '</div>'
        + '<div class="idetail__foot"><div class="idetail__stat"><span class="v">' + esc(cur.stat.value) + '</span> <span class="l">' + esc(cur.stat.label) + '</span></div>'
        + '<a class="idetail__study" href="#">' + esc(cur.study) + ' <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></a>'
        + '</div></div></div>';
    }
  }

  /* ---------- future: plug in your biology ---------- */
  function buildFuture(root, goals) {
    if (!goals.length) return;
    var rail = root.querySelector("#fut-rail");
    var stage = root.querySelector("#fut-stage");
    if (!rail || !stage) return;
    var active = goals[0].id;
    goals.forEach(function (g) {
      var b = document.createElement("button");
      b.className = "fut-goal" + (g.flagship ? " flagship" : ""); b.style.setProperty("--sa", hueVar(g.hue)); b.setAttribute("data-id", g.id);
      b.innerHTML = '<span class="fut-goal__dot"></span>'
        + '<span class="fut-goal__txt"><span class="fut-goal__name">' + esc(g.name) + '</span><span class="fut-goal__code">' + esc(g.code) + '</span></span>'
        + (g.flagship ? '<span class="fut-goal__star">' + sparkSVG(12) + '</span>' : '')
        + '<svg class="fut-goal__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      b.addEventListener("click", function () { select(g.id); });
      rail.appendChild(b);
    });
    function select(id) {
      active = id;
      var g = null; goals.forEach(function (x) { if (x.id === id) g = x; });
      [].slice.call(rail.children).forEach(function (c) {
        var on = c.getAttribute("data-id") === id; c.classList.toggle("on", on); c.setAttribute("aria-selected", on);
      });
      stage.innerHTML = goalHTML(g);
    }
    function goalHTML(g) {
      var aa = g.peptide.map(function (p, i) { return '<span class="fut-aa" style="animation-delay:' + (0.12 + i * 0.13) + 's">' + esc(p) + '</span>'; }).join("");
      return '<div class="fut-detail" style="--sa:' + hueVar(g.hue) + '">'
        + '<div class="fut-detail__head"><div class="fut-detail__id"><span class="fut-detail__code">' + esc(g.code) + '</span>'
        + (g.flagship ? '<span class="fut-flag">' + sparkSVG(12) + ' Flagship</span>' : '') + '</div><span class="fut-detail__eta">' + esc(g.eta) + '</span></div>'
        + '<h3 class="fut-detail__name">' + esc(g.name) + '</h3><p class="fut-detail__tag">' + esc(g.tagline) + '</p>'
        + '<div class="fut-install"><span class="fut-install__prompt">$</span><span class="fut-install__cmd">' + esc(g.install) + '</span><span class="fut-install__ok">' + checkSVG(13) + ' ready</span></div>'
        + '<div class="fut-module"><span class="fut-module__socket">' + plugSVG(18) + '</span><span class="fut-module__wire"></span><span class="fut-module__chain">' + aa + '</span></div>'
        + '<p class="fut-detail__what">' + esc(g.what) + '</p>'
        + '<div class="fut-detail__metric"><div class="fut-metric__head"><span class="fut-metric__v">' + esc(g.metric.v) + '</span><span class="fut-metric__l">' + esc(g.metric.l) + '</span></div><div class="fut-metric__track"><i class="fut-metric__fill"></i></div></div>'
        + '</div>';
    }
    select(active);
  }

  /* ---------- code editor typer ---------- */
  function setupTyper(root) {
    var host = root.querySelector("[data-typer]");
    if (!host) return;
    var MOTIF = ["Gly", "Pro", "Hyp"], MAX = 9, count = 0;
    function render() {
      var s = "";
      for (var i = 0; i < count; i++) s += (i > 0 ? '<span class="cb-op"> · </span>' : "") + '<span class="cb-acid">' + MOTIF[i % 3] + "</span>";
      host.innerHTML = s + '<span class="cb__cursor"></span>';
    }
    render();
    setInterval(function () { count = count >= MAX ? 0 : count + 1; render(); }, 520);
  }

  /* ---------- mobile read-more clamp ---------- */
  function setupClamp(root) {
    var clamps = [].slice.call(root.querySelectorAll(".clamp"));
    function check() {
      var mobile = window.matchMedia("(max-width: 600px)").matches;
      clamps.forEach(function (c) {
        var t = c.querySelector(".clamp__t"), btn = c.querySelector(".clamp__btn");
        if (!t) return;
        if (!mobile) { c.classList.remove("more"); return; }
        var over = t.scrollHeight - t.clientHeight > 4 || c.classList.contains("open");
        c.classList.toggle("more", over);
        if (btn && !btn.__wired) {
          btn.__wired = true;
          btn.addEventListener("click", function (e) {
            e.preventDefault(); e.stopPropagation();
            c.classList.toggle("open");
            btn.textContent = c.classList.contains("open") ? "Read less" : "Read more";
          });
        }
      });
    }
    check();
    window.addEventListener("resize", debounce(check, 200));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
  }

  /* ---------- sticky shop bar ---------- */
  function setupShopBar(root) {
    var bar = root.querySelector(".shopbar");
    if (!bar) return;
    function onScroll() {
      var st = window.scrollY || document.documentElement.scrollTop;
      var sh = document.documentElement.scrollHeight, ch = window.innerHeight;
      var nearBottom = sh - (st + ch) < 280;
      bar.classList.toggle("show", st > 420 && !nearBottom);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ---------- helpers ---------- */
  function pad(n) { return n < 10 ? "0" + n : "" + n; }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
  function debounce(fn, ms) { var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); }; }
  function sparkSVG(s) { return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="none"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>'; }
  function checkSVG(s) { return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'; }
  function plugSVG(s) { return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="none"><path d="M9 3v5M15 3v5M7 8h10v3a5 5 0 01-10 0V8zM12 16v5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>'; }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  document.addEventListener("shopify:section:load", function (e) {
    if (e.target.querySelector(".science-page-section")) init();
  });
})();
